import { useState } from "react";
import { Button, Form, Card, Modal, Alert } from "react-bootstrap";
import {
  SiteLevelMeasurements,
  SourceLevelMeasurements,
  DetectionTechnology,
  EnvironmentalConditions
} from "../AddSiteMeasurement/sections";
import { PreliminaryReconciliationAssessment } from "../AddSiteMeasurement/sections/PreliminaryReconciliationAssessment";
import { useMutation } from "@apollo/client";
import { UPDATE_SITE_MEASUREMENT } from "../../../../graphql/mutations";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

const EditSiteMeasurementModal = ({
  show,
  viewOnly,
  siteMeasurement,
  handleClose,
  env,
  handleEnvChange,
  currentTenant,
  currentTenantName,
  currentGroupVersion,
  versionsCategories = [],
  ogmpCategories = [],
  measurementTech = []
}) => {
  const navigate = useNavigate();
  const [updateSiteMeasurement, { loading: mutationLoading }] = useMutation(UPDATE_SITE_MEASUREMENT);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // calculate reconciliation status
  const calculateReconciliationStatus = () => {
    if (siteData.reconciliationStatus !== "READY") {
      return "DATA_PENDING";
    }

    const siteEmissions = parseFloat(siteData.emissions || 0);
    const siteLevelUncertainty = parseFloat(siteData.siteLevelUncertainty || 0);
    const sourceLevelUncertainty = parseFloat(siteData.sourceLevelUncertainty || 0);

    // calculate total emissions from all sources
    const totalSourceEmissions = sources
      .filter((source) => source.sourceCategoryId && source.emissions)
      .reduce((sum, source) => sum + parseFloat(source.emissions || 0), 0);

    const site_start = (1 - siteLevelUncertainty) * siteEmissions;
    const site_end = (1 + siteLevelUncertainty) * siteEmissions;

    const source_start = (1 - sourceLevelUncertainty) * totalSourceEmissions;
    const source_end = (1 + sourceLevelUncertainty) * totalSourceEmissions;

    if (site_start <= source_end && source_start <= site_end) {
      return "RECONCILED";
    } else {
      return "NOT_RECONCILED";
    }
  };

  const [siteData, setSiteData] = useState({
    siteId: siteMeasurement.project.id,
    measurementDateTime: siteMeasurement.measurementTimestamp,
    siteName: siteMeasurement.project.name,
    startTime: new Date(`${siteMeasurement.measurementTimestamp.split("T")[0]}T${siteMeasurement.startTime}`),
    endTime: new Date(`${siteMeasurement.measurementTimestamp.split("T")[0]}T${siteMeasurement.endTime}`),
    emissions: siteMeasurement.emissions,
    emissionsUnit: siteMeasurement.emissionsUnit,
    siteLevelUncertainty: siteMeasurement.uncertaintyFraction,
    sourceLevelUncertainty: siteMeasurement.sourceLevelUncertaintyFraction,
    minimumDetectionLimit: siteMeasurement.minimumDetectionLimit,
    minimumDetectionUnit: siteMeasurement.minimumDetectionUnit,
    summary: siteMeasurement.summary,
    reconciliationStatus: siteMeasurement.reconciliationReadyStatus
  });

  const siteOptions = [{ value: siteMeasurement.project.name, label: siteMeasurement.project.name }];

  const [sources, setSources] = useState(
    siteMeasurement.sourceMeasurements.map((sourceMeasurement) => ({
      id: sourceMeasurement.id,
      sourceCategoryId: versionsCategories.find((vc) => vc.id === sourceMeasurement.versionOgmpCategoryId)?.ogmpCategoryId,
      emissions: sourceMeasurement.emissions,
      emissionsUnit: sourceMeasurement.emissionsUnit,
      summary: sourceMeasurement.summary,
      versionOgmpCategoryId: sourceMeasurement.versionOgmpCategoryId
    }))
  );

  const [techData, setTechData] = useState({
    technologyId: siteMeasurement.measurementTechnologyId,
    provider: siteMeasurement.provider,
    detectionThreshold: siteMeasurement.detectionThreshold,
    spatialResolution: siteMeasurement.spatialResolution
  });

  const [envData, setEnvData] = useState({
    windSpeed: siteMeasurement.windSpeed,
    windSpeedUnit: siteMeasurement.windSpeedUnit,
    temperature: siteMeasurement.temperature,
    temperatureUnit: siteMeasurement.temperatureUnit,
    environmentalUncertainty: siteMeasurement.environmentalUncertainty
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // check if this is a reconciled measurement and show warning on first attempt
    const reconciliationStatus = calculateReconciliationStatus();
    if (reconciliationStatus === "RECONCILED" && !submitAttempted) {
      setSubmitAttempted(true);
      setShowWarningModal(true);
      return;
    }
    await editSiteMeasurement();
  };

  const editSiteMeasurement = async () => {
    const sourceMeasurementsPayload = sources.map((source) => ({
      id: source.id,
      emissions: Number(source.emissions),
      emissionsUnit: source.emissionsUnit,
      summary: source.summary,
      versionOgmpCategoryId: source.versionOgmpCategoryId
    }));

    const siteMeasurementInputPayload = {
      projectId: siteData.siteId,
      emissions: Number(siteData.emissions),
      emissionsUnit: siteData.emissionsUnit,
      measurementTechnologyId: techData.technologyId ? techData.technologyId : null,
      measurementProvider: techData.provider,
      detectionThreshold: techData.detectionThreshold,
      spatialResolution: techData.spatialResolution,
      groupVersionId: currentGroupVersion,
      measurementTimestamp: siteData.measurementDateTime,
      startTime: siteData.startTime.toISOString().split("T")[1],
      endTime: siteData.endTime.toISOString().split("T")[1],
      windSpeed: Number(envData.windSpeed),
      windSpeedUnit: envData.windSpeedUnit,
      temperature: Number(envData.temperature),
      temperatureUnit: envData.temperatureUnit,
      environmentalUncertainty: envData.environmentalUncertainty,
      summary: siteData.summary,
      uncertaintyFraction: Number(siteData.siteLevelUncertainty),
      sourceLevelUncertaintyFraction: Number(siteData.sourceLevelUncertainty),
      sourceMeasurements: sourceMeasurementsPayload,
      reconciliationReadyStatus: siteData.reconciliationStatus,
      minimumDetectionLimit: siteData.minimumDetectionLimit === "" ? null : Number(siteData.minimumDetectionLimit),
      minimumDetectionUnit: siteData.minimumDetectionUnit ? siteData.minimumDetectionUnit : null
    };
    try {
      const { data } = await updateSiteMeasurement({
        variables: {
          id: siteMeasurement.id,
          tenantId: currentTenant,
          siteMeasurementInput: siteMeasurementInputPayload
        }
      });
      if (data) {
        toast.success(`Site Measurement created successfully with ID: ${data?.createSiteMeasurement}`);
      } else {
        toast.error("Error creating tenant");
      }
    } catch {
      setTimeout(() => {
        toast.error("Failed to add new site measurements");
      }, 200);
    } finally {
      handleEnvChange(env, currentTenant, currentTenantName, currentGroupVersion);
      navigate("/measurements");
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered fullscreen={true}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Site Measurement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="border mb-3" style={{ maxHeight: "800px", overflowY: "auto" }}>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <SiteLevelMeasurements
                  siteData={siteData}
                  setSiteData={setSiteData}
                  sources={sources}
                  setSources={setSources}
                  siteOptions={siteOptions}
                />

                <SourceLevelMeasurements
                  sources={sources}
                  setSources={setSources}
                  versionsCategories={versionsCategories}
                  ogmpCategories={ogmpCategories}
                />

                <DetectionTechnology techData={techData} setTechData={setTechData} measurementTech={measurementTech} />

                <EnvironmentalConditions envData={envData} setEnvData={setEnvData} />

                <PreliminaryReconciliationAssessment siteData={siteData} sources={sources} />

                <div className="d-grid">
                  {!viewOnly && (
                    <Button
                      variant="primary"
                      type="submit"
                      className="mt-3 mb-3 mx-auto"
                      style={{ width: "25%" }}
                      disabled={mutationLoading}
                    >
                      {mutationLoading ? "Editing measurement..." : "Save Measurement"}
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Modal.Body>
      </Modal>
      {/* Warning Modal for Reconciled Measurements */}
      <Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
            Reconciled Measurement Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <p>
              <strong>This measurement has been preliminarily marked as RECONCILED.</strong>
            </p>
            <p>
              This means that the site-level measurements and source-level measurements overlap within their respective uncertainty ranges,
              which is a positive indicator of data quality.
            </p>
            <p>Please review all entered data to ensure accuracy before final submission.</p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowWarningModal(false);
              setSubmitAttempted(false);
            }}
          >
            Review Data
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowWarningModal(false);
              setSubmitAttempted(false);
              editSiteMeasurement();
            }}
          >
            Confirm & Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export { EditSiteMeasurementModal };
